import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import Checkmark from '@splunk/react-icons/Checkmark';
import WaitSpinner from '@splunk/react-ui/WaitSpinner';
import P from '@splunk/react-ui/Paragraph';
import { _ } from '@splunk/ui-utils/i18n';
import { sprintf } from '@splunk/ui-utils/format';
import { useDashboardApi } from '@splunk/dashboard-context';
import Message from '@splunk/react-ui/Message';

interface SearchProgressArgType {
    onSearchProgressComplete: () => void;
    isSearchProgressComplete: boolean;
    searchProgressInterval?: number;
}

export const Row = styled('div')`
    display: flex;
    column-gap: 8px;
    align-items: flex-start;
    margin-bottom: ${variables.spacingMedium};
    margin-top: ${variables.spacingMedium};
`;

export const StyledWaitSpinner = styled(WaitSpinner)`
    flex-grow: 0;
    margin-top: 2px;
`;

export const TextContainer = styled('div')`
    flex-grow: 1;
`;

const RunningSearchesText = styled(P)`
    margin-bottom: 0;
`;

export const CompletionText = styled(P)`
    font-size: ${variables.fontSizeSmall};
    line-height: 20px;
    margin-bottom: 0;
`;

const StyledCheckmark = styled(Checkmark)`
    color: ${variables.statusColorNormal};
`;

const StyledMessage = styled(Message)`
    margin-top: ${variables.spacingLarge};
`;

const SEARCH_PROGRESS_TEXT = _(
    '%(completedSearches)d out of %(totalSearches)d searches completed.'
);
const RUNNING_SEARCHES_TEXT = _('Running all searches ...');
const DOWNLOAD_ANYWAY_TEXT = _(
    "Note: Selecting 'Download anyway' may lead to partial results in the downloaded dashboard."
);

export const SearchProgress = ({
    isSearchProgressComplete,
    onSearchProgressComplete,
    searchProgressInterval = 1000,
}: SearchProgressArgType) => {
    const dashboardApi = useDashboardApi();
    const [totalSearches, setTotalSearches] = useState<number>();
    const [completedSearches, setCompletedSearches] = useState<number>();

    useEffect(() => {
        const interval = setInterval(() => {
            const allSearches = dashboardApi.getSearches();
            const allSearchesWithStatus = Object.values(allSearches).filter(
                (search) => !!search.state?.meta?.status
            );
            const runningSearches = dashboardApi
                .getRunningSearches()
                .filter((search) => !search.state?.meta?.isRealTimeSearch);

            const newCompletedSearches =
                allSearchesWithStatus.length - runningSearches.length;
            setCompletedSearches(newCompletedSearches);
            setTotalSearches(allSearchesWithStatus.length);

            if (newCompletedSearches === allSearchesWithStatus.length) {
                onSearchProgressComplete();
                clearInterval(interval);
            }
        }, searchProgressInterval);

        return () => {
            clearInterval(interval);
        };
    }, [dashboardApi, onSearchProgressComplete, searchProgressInterval]);

    const searchProgressText =
        typeof completedSearches !== 'undefined' &&
        typeof totalSearches !== 'undefined'
            ? sprintf(SEARCH_PROGRESS_TEXT, {
                  completedSearches,
                  totalSearches,
              })
            : '';

    return (
        <>
            <Row>
                {isSearchProgressComplete ? (
                    <StyledCheckmark width={19} height={19} />
                ) : (
                    <StyledWaitSpinner />
                )}
                <TextContainer>
                    <RunningSearchesText>
                        {RUNNING_SEARCHES_TEXT}
                    </RunningSearchesText>
                    <CompletionText>{searchProgressText}</CompletionText>
                </TextContainer>
            </Row>
            {!isSearchProgressComplete && (
                <StyledMessage type="info">
                    {DOWNLOAD_ANYWAY_TEXT}
                </StyledMessage>
            )}
        </>
    );
};
